import { useEffect, useState } from "react";
import api from "@/lib/api";
import StatCard from "@/components/StatCard";
import {
  Building2, UserCheck, Factory, FileText, CheckCircle2, AlertTriangle, Bell, MessageSquareText, Zap,
} from "lucide-react";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";

const PIE_COLORS = { green: "#22C55E", amber: "#F59E0B", red: "#EF4444", blue: "#3B82F6" };
const PERF_COLORS = { "On Time": "#22C55E", "Late": "#EF4444", "In Progress": "#3B82F6" };

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Loading admin dashboard…</div>;
  const c = data.counts;

  const cards = [
    { label: "Organizations", value: c.organizations, icon: Building2, id: "adm-orgs" },
    { label: "Buyers", value: c.buyers, icon: UserCheck, id: "adm-buyers" },
    { label: "Suppliers", value: c.suppliers, icon: Factory, id: "adm-suppliers" },
    { label: "Active POs", value: c.active_pos, icon: FileText, tone: "blue", id: "adm-active" },
    { label: "Delivered POs", value: c.delivered_pos, icon: CheckCircle2, tone: "green", id: "adm-delivered" },
    { label: "Delayed POs", value: c.delayed_pos, icon: AlertTriangle, tone: "red", id: "adm-delayed" },
    { label: "Pending Actions", value: c.pending_actions, icon: Bell, tone: "amber", id: "adm-pending" },
    { label: "Messages Sent", value: c.messages_sent, icon: MessageSquareText, id: "adm-messages" },
    { label: "Automations Triggered", value: c.automations_triggered, icon: Zap, tone: "blue", id: "adm-automations" },
  ];

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight">Admin Dashboard</h1>
        <p className="text-sm text-muted-foreground">Is the procurement organization using the system effectively, and is supplier execution improving?</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-5">
        {cards.map((k, i) => (
          <div key={k.id} className="animate-fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid={`admin-kpi-${k.id}`}>
            <StatCard label={k.label} value={k.value} icon={k.icon} tone={k.tone || "slate"} testId={k.id} />
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">PO Status Distribution</h2>
          <div className="h-64 min-h-64 p-2" data-testid="adm-status-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <PieChart>
                <Pie data={data.status_distribution.filter((s) => s.count > 0)} dataKey="count" nameKey="label" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {data.status_distribution.filter((s) => s.count > 0).map((s) => <Cell key={s.label} fill={PIE_COLORS[s.color] || "#94A3B8"} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Monthly PO Volume</h2>
          <div className="h-64 min-h-64 p-4" data-testid="adm-volume-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <BarChart data={data.monthly_volume}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#0F172A" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Open PO Ageing</h2>
          <div className="h-64 min-h-64 p-4" data-testid="adm-ageing-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <BarChart data={data.ageing}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.ageing.map((b, i) => <Cell key={b.bucket} fill={["#22C55E", "#3B82F6", "#F59E0B", "#EF4444"][i]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border bg-card">
          <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Delivery Performance</h2>
          <div className="h-64 min-h-64 p-2" data-testid="adm-delivery-chart">
            <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
              <PieChart>
                <Pie data={data.delivery_performance} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {data.delivery_performance.map((d) => <Cell key={d.name} fill={PERF_COLORS[d.name]} />)}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-sm font-semibold">Supplier Response Time (avg hours to acknowledge)</h2>
        <div className="h-64 min-h-64 p-4" data-testid="adm-response-chart">
          <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
            <BarChart data={data.supplier_response} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" width={90} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => `${v}h`} />
              <Bar dataKey="hours" fill="#3B82F6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
